import React, { useState } from 'react'
import dayjs from 'dayjs'
import { isNumber, isString } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { copyButtonIconColour } from 'styles/theme/colours'
import {
  Button,
  Box as Code,
  Box as Column,
  CopyButton,
  Box as Heading,
  Box as Key,
  Box as KeyValueRow,
  Box as MainArea,
  Box as Section,
  Box as ShowHide,
  Text,
  Box as Value,
} from '@node-space/storybook-components'
import { ellipseMiddleText } from '@node-space/utils'
import { PaymentsResponse } from 'types/payments'
import { sentenceCase, shortenString, sortObjectPropertiesByName } from 'utils/utils'

interface PaymentDetailsProps {
  transaction: PaymentsResponse
}

const PaymentDetails = ({ transaction }: PaymentDetailsProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const amountDue = transaction?.walletCurrency
    ? transaction.paidCurrency?.amount - transaction.paidCurrency?.actual
    : 0

  const dataColumns = [
    {
      heading: `${t('general')}:`,
      items: [
        {
          label: `${t('reference')}:`,
          value: (
            <Value flex>
              <Text size="sm" color="black">
                {shortenString(transaction?.reference, 25)}
              </Text>
              <CopyButton
                copyText={String(transaction?.reference)}
                iconColour={copyButtonIconColour}
              />
            </Value>
          ),
        },
        {
          label: `${t('status')}:`,
          value: transaction?.status,
        },
        {
          label: `${t('type')}:`,
          value: transaction?.type,
        },
        {
          label: `UUID:`,
          value: (
            <Value flex>
              <Text size="sm" color="black">
                {shortenString(transaction?.uuid, 20)}
              </Text>
              <CopyButton copyText={transaction?.uuid} iconColour={copyButtonIconColour} />
            </Value>
          ),
        },
        {
          label: `${t('createdDate')}:`,
          value: dayjs(transaction?.dateCreated).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
          label: `${t('expiryDate')}:`,
          value: dayjs(transaction?.expiryDate).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
          label: `${t('hash')}:`,
          value: (
            <Value flex>
              <Text size="sm" color="black">
                {ellipseMiddleText(transaction?.transactions[0]?.hash, 6, 6) || '-'}
              </Text>
              {transaction?.transactions[0]?.hash && (
                <CopyButton
                  copyText={transaction?.transactions[0]?.hash}
                  iconColour={copyButtonIconColour}
                />
              )}
            </Value>
          ),
        },
      ],
    },
    {
      heading: `${t('quote')}:`,
      items: [
        {
          label: `${t('from')}:`,
          value: transaction?.exchangeRate?.base,
        },
        {
          label: `${t('amountIn')}:`,
          value: transaction?.walletCurrency?.actual,
        },
        {
          label: `${t('to')}:`,
          value: transaction?.exchangeRate?.counter,
        },
        {
          label: `${t('amountDue')}:`,
          value: amountDue,
        },
        {
          label: `${t('expiryDate')}:`,
          value: dayjs(transaction?.expiryDate).format('YYYY-MM-DD HH:mm:ss'),
        },
      ],
    },
    {
      heading: `${t('payment')}:`,
      items: [
        {
          label: `${t('currency')}:`,
          value: transaction?.displayCurrency?.currency,
        },
        {
          label: `${t('amount')}:`,
          value: transaction?.displayCurrency?.amount,
        },
        {
          label: `${t('paid')}:`,
          value: transaction?.displayCurrency?.actual,
        },
        {
          label: `${t('walletCurrency')}:`,
          value: transaction?.walletCurrency?.currency,
        },
        {
          label: `${t('walletNetAmount')}:`,
          value: transaction?.walletCurrency?.actual,
        },
        {
          label: `${t('walletAmount')}:`,
          value: transaction?.walletCurrency?.amount,
        },
      ],
    },
  ]
  return (
    <>
      <MainArea className="xs:w-23 grid xs:grid-cols-1 xs:space-y-4 sm:gap-6 sm:grid-cols-3 sm:justify-between sm:space-y-0 lg:grid-cols-3 lg:justify-between lg:space-y-0">
        {!!dataColumns?.length &&
          dataColumns?.map(column => {
            return (
              <Column
                key={column?.heading}
                flex
                flexAuto
                flexCol
                className="xs:w-4/6 space-y-1 sm:w-full"
              >
                <Heading
                  flex
                  justifyContent="between"
                  paddingB={8}
                  divider="bottom"
                  className="mb-1"
                >
                  <Text size="sm" weight="medium" color="black">
                    {column?.heading}
                  </Text>
                </Heading>

                {!!column?.items?.length &&
                  column?.items?.map(({ label, value }) => {
                    return (
                      <KeyValueRow
                        key={String(label)}
                        flex
                        justifyContent="between"
                        paddingX={4}
                        borderRadius={4}
                        className="mb-1 hover:bg-gray-02"
                      >
                        <Key>
                          {!isString(label) && !isNumber(label) ? (
                            <>{label || '-'}</>
                          ) : (
                            <Text size="sm" color="grey">
                              {sentenceCase(String(label)) || '-'}
                            </Text>
                          )}
                        </Key>
                        <Value>
                          {!isString(value) && !isNumber(value) ? (
                            <>{value || '-'}</>
                          ) : (
                            <Text size="sm" color="black">
                              {String(value) || '-'}
                            </Text>
                          )}
                        </Value>
                      </KeyValueRow>
                    )
                  })}
              </Column>
            )
          })}
      </MainArea>
      <Section className="mt-6">
        <Button noStyling className="flex" onClick={() => setIsExpanded(!isExpanded)}>
          <Text weight="medium" size="sm">
            {t('fullPayload', {
              toggle: isExpanded ? t('hide') : t('view'),
            })}
          </Text>
        </Button>
        <ShowHide flex className={`my-4 animate-fade-in-down ${isExpanded ? `block` : `hidden`}`}>
          <Code
            tag="pre"
            flex
            full
            className="monospace font-bold p-4 text-left text-white text-xs bg-black rounded-xl"
          >
            {JSON.stringify(sortObjectPropertiesByName(transaction), null, 3)}
          </Code>
        </ShowHide>
      </Section>
    </>
  )
}

export default React.memo(PaymentDetails)
